import { Button, Form, Popconfirm, Modal, Checkbox } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AdnTable from "../adnTable/AdnTable";
import AdnAutoComplete from "../adnAutoComplete/AdnAutoComplete";
import {
  getUserCertifications,
  addUserCertification,
  deleteUserCertification,
  getCategoryCourseList,
  getCourseTypeList,
  getCourseTypeSubList,
} from "../../../api/CertificationApi";
import AdnMessageSuccess from "../adnMessage/AdnMessageSuccess";
import AdnMessageError from "../adnMessage/AdnMessageError";
import AdnDatePicker from "../adnDatePicker/AdnDatePicker";
import { Departement, dateFormat } from "../GeneralConfigurations";
import moment from "moment";
import { getAuthenticatedUserId } from "../../../api/ApiUtils";
import { getUserById } from "../../../api/UserApi";

const AdnCertificationTable = ({ employee, admissionDate }) => {
  const { t } = useTranslation();
  //ESTADOS
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalState, setModalState] = useState("");
  const [categoryCourseList, setCategoryCourseList] = useState([]);
  const [courseTypeList, setCourseTypeList] = useState([]);
  const [courseTypeSubList, setCourseTypeSubList] = useState([]);
  const [filteredCategoryList, setFilteredCategoryList] = useState([]);
  const [filteredCourseTypeList, setFilteredCourseTypeList] = useState([]);
  const [filteredCourseSubList, setFilteredCourseSubList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [selectedCourseType, setSelectedCourseType] = useState();
  const [isManager, setIsManager] = useState(true);

  const [formCertifications] = Form.useForm();

  const disabledStartDate = (current) => {
    if (!admissionDate) return false;
    return current && current > moment(admissionDate).endOf("day");
  };

  useEffect(() => {
    getUserById(getAuthenticatedUserId()).then((res) => {
      const NexelOrChiefIds = [1, 2, 3, 20];
      if (
        res.data.departmentId === Departement.Manager &&
        !NexelOrChiefIds.includes(res.data.positionId)
      ) {
        setIsManager(true);
      } else {
        setIsManager(false);
      }
    });
  }, []);

  //COLUNAS
  const columns = [
    {
      title: t("certificationTable.columns.certificationName"),
      dataIndex: "certificationName",
      width: "60%",
    },
    {
      title: t("certificationTable.columns.completionDate"),
      dataIndex: "start",
      width: "20%",
      render: (text, record) => (
        <div>
          {record.start ? moment(record.start).format(dateFormat) : null}
        </div>
      ),
    },
    {
      title: t("certificationTable.columns.beforeAdmission"),
      dataIndex: "beforeAdmission",
      width: "15%",
      render: (_, record) => (
        <Checkbox checked={record.beforeAdmission} disabled />
      ),
    },
    {
      title: t("certificationTable.columns.actions"),
      dataIndex: "operation",
      width: "5%",
      render: (_, record) => (
        <>
          {!isManager && record.beforeAdmission !== false && (
            <Popconfirm
              title={t("certificationTable.messages.deleteConfirmation")}
              onConfirm={() => handleDelete(record.certificationId)}
            >
              <Button
                icon={<DeleteOutlined></DeleteOutlined>}
                type="danger"
              ></Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  //FUNÇÕES

  const handleDelete = (certificationId) => {
    setLoading(true);
    deleteUserCertification(certificationId)
      .then((res) => {
        if (res && res.status === 200) {
            getUserCertifications({ employeeId: employee, pageSize: 2147483647 }).then((resGet) => {
            if (resGet && resGet.data) {
              setDataSource(resGet.data.result || []);
            }
          });
          AdnMessageSuccess();
        } else {
          AdnMessageError(
            t("certificationTable.messages.cantDeleteCertification")
          );
        }
      })
      .catch((error) => {
        console.error("Error deleting certification:", error);
        AdnMessageError(
          t("certificationTable.messages.cantDeleteCertification")
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setModalState(t("certificationTable.modals.addCertification"));
    setModalVisible(true);
    loadCategoryCourses();
  };

  const loadCategoryCourses = () => {
    getCategoryCourseList()
      .then((res) => {
        if (res && res.status === 200) {
          setCategoryCourseList(res.data || []);
          setFilteredCategoryList(res.data || []);
        }
      })
      .catch((error) => {
        console.error("Error loading categories:", error);
        setCategoryCourseList([]);
        setFilteredCategoryList([]);
      });
  };

  const handleSearchCategory = (searchText) => {
    if (!searchText) {
      setFilteredCategoryList(categoryCourseList);
      return;
    }
    const filtered = categoryCourseList.filter((cat) =>
      cat.text.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredCategoryList(filtered);
  };

  const handleClearCategory = () => {
    setSelectedCategory(null);
    setCourseTypeList([]);
    setCourseTypeSubList([]);
    setFilteredCourseTypeList([]);
    setFilteredCourseSubList([]);
    formCertifications.resetFields(["courseTypeId", "courseTypeSubId"]);
  };

  const handleCategoryChange = (categoryText) => {
    const selectedCat = categoryCourseList.find(
      (cat) => cat.text === categoryText
    );
    if (selectedCat) {
      console.log("Category selected:", selectedCat);
      setSelectedCategory(selectedCat.value);
      setSelectedCourseType(null);
      setCourseTypeList([]);
      setCourseTypeSubList([]);
      setFilteredCourseTypeList([]);
      setFilteredCourseSubList([]);
      formCertifications.resetFields(["courseTypeId", "courseTypeSubId"]);

      console.log("Fetching course types for category:", selectedCat.value);
      getCourseTypeList({ categoryCourseId: selectedCat.value })
        .then((res) => {
          console.log("Course types response:", res);
          if (res && res.status === 200) {
            setCourseTypeList(res.data || []);
            setFilteredCourseTypeList(res.data || []);
          }
        })
        .catch((error) => {
          console.error("Error loading course types:", error);
          setCourseTypeList([]);
          setFilteredCourseTypeList([]);
        });
    }
  };

  const handleSearchCourseType = (searchText) => {
    if (!searchText) {
      setFilteredCourseTypeList(courseTypeList);
      return;
    }
    const filtered = courseTypeList.filter((type) =>
      type.text.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredCourseTypeList(filtered);
  };

  const handleClearCourseType = () => {
    setSelectedCourseType(null);
    setCourseTypeSubList([]);
    setFilteredCourseSubList([]);
    formCertifications.resetFields(["courseTypeSubId"]);
  };

  const handleCourseTypeChange = (courseTypeText) => {
    const selectedType = courseTypeList.find(
      (type) => type.text === courseTypeText
    );
    if (selectedType) {
      setSelectedCourseType(selectedType.value);
      setCourseTypeSubList([]);
      setFilteredCourseSubList([]);
      formCertifications.resetFields(["courseTypeSubId"]);
      getCourseTypeSubList({ courseTypeId: selectedType.value })
        .then((res) => {
          if (res && res.status === 200) {
            setCourseTypeSubList(res.data || []);
            setFilteredCourseSubList(res.data || []);
          }
        })
        .catch((error) => {
          console.error("Error loading course sub types:", error);
          setCourseTypeSubList([]);
          setFilteredCourseSubList([]);
        });
    }
  };

  const handleSearchCourseSub = (searchText) => {
    if (!searchText) {
      setFilteredCourseSubList(courseTypeSubList);
      return;
    }
    const filtered = courseTypeSubList.filter((sub) =>
      sub.text.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredCourseSubList(filtered);
  };

  const handleSave = async (values) => {
    if (!selectedCategory) {
      AdnMessageError(t("certificationTable.errors.pleaseSelectCategory"));
      return;
    }

    if (!selectedCourseType) {
      AdnMessageError(t("certificationTable.errors.pleaseSelectCourseType"));
      return;
    }

    let courseTypeSubId = null;

    if (values.courseTypeSubId && courseTypeSubList.length > 0) {
      const selectedCourse = courseTypeSubList.find(
        (course) => course.text === values.courseTypeSubId
      );
      if (selectedCourse) {
        courseTypeSubId = parseInt(selectedCourse.value);
      }
    } else {
      courseTypeSubId = parseInt(selectedCourseType);
    }

    if (!courseTypeSubId) {
      AdnMessageError(
        t("certificationTable.errors.processingCertificationSelection")
      );
      return;
    }

    const userCertification = {
      employeeId: employee,
      categoryCourseId: parseInt(selectedCategory),
      courseTypeId: parseInt(selectedCourseType),
      courseTypeSubId: courseTypeSubId,
      start: values.startDate,
      beforeAdmission: true,
    };

    setLoading(true);
    setModalVisible(false);

    addUserCertification(userCertification)
      .then((res) => {
        if (res && res.status === 200) {
          getUserCertifications({ employeeId: employee, pageSize: 2147483647 }).then((res2) => {
            if (res2 && res2.data) {
              setDataSource(res2.data.result || []);
            }
            formCertifications.resetFields();
          });
          AdnMessageSuccess();
        } else {
          AdnMessageError(
            t("certificationTable.messages.cantDeleteCertification")
          );
        }
      })
      .catch((error) => {
        console.error("Error adding certification:", error);
        AdnMessageError(
          t("certificationTable.messages.cantDeleteCertification")
        );
      })
      .finally(() => {
        setLoading(false);
      });

    formCertifications.resetFields();
    setModalState("");
  };

  const handleModalCancel = () => {
    formCertifications.resetFields();
    setModalVisible(false);
    setSelectedCategory(null);
    setSelectedCourseType(null);
    setCourseTypeList([]);
    setCourseTypeSubList([]);
  };

  useEffect(() => {
    if (employee) {
      setLoading(true);
        getUserCertifications({ employeeId: employee, pageSize: 2147483647 })
        .then((res) => {
          if (res && res.status === 200) {
            setDataSource(res.data.result || []);
          }
        })
        .catch((error) => {
          console.error("Error loading certifications:", error);
          setDataSource([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [employee]);

  return (
    <>
      {!isManager && (
        <div>
          <Button
            className="adn-primary-button"
            onClick={handleAdd}
            type="primary"
            style={{
              marginBottom: 8,
            }}
          >
            {t("certificationTable.buttons.addCertification")}
          </Button>
        </div>
      )}
      <AdnTable
        columns={columns}
        data={dataSource}
        rowClassName={() => "editable-row"}
        bordered
        loading={loading}
      />
      <Modal
        title={modalState}
        open={modalVisible}
        onOk={formCertifications.submit}
        okText={t("certificationTable.buttons.save")}
        cancelText={t("certificationTable.buttons.back")}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form form={formCertifications} onFinish={handleSave} layout="vertical">
          <Form.Item
            label={t("certificationTable.labels.category")}
            name="categoryId"
          >
            <AdnAutoComplete
              placeholder={t("certificationTable.placeholders.selectCategory")}
              valueId={"text"}
              resultList={filteredCategoryList}
              handleSearch={handleSearchCategory}
              handleSelect={handleCategoryChange}
              handleClear={handleClearCategory}
              allowClear={true}
            />
          </Form.Item>

          <Form.Item
            label={t("certificationTable.labels.certification")}
            name="courseTypeId"
          >
            <AdnAutoComplete
              placeholder={t(
                "certificationTable.placeholders.selectCertification"
              )}
              valueId={"text"}
              resultList={filteredCourseTypeList}
              handleSearch={handleSearchCourseType}
              handleSelect={handleCourseTypeChange}
              handleClear={handleClearCourseType}
              allowClear={true}
              disabled={!selectedCategory}
            />
          </Form.Item>

          {courseTypeSubList.length > 0 && (
            <Form.Item
              label={t("certificationTable.labels.version")}
              name="courseTypeSubId"
            >
              <AdnAutoComplete
                placeholder={t("certificationTable.placeholders.selectVersion")}
                valueId={"text"}
                resultList={filteredCourseSubList}
                handleSearch={handleSearchCourseSub}
                allowClear={true}
                disabled={!selectedCourseType}
              />
            </Form.Item>
          )}

          <AdnDatePicker
            name="startDate"
            label={t("certificationTable.labels.completionDate")}
            placeholder={t("certificationTable.placeholders.completionDate")}
            format={dateFormat}
            defaultPickerValue={
              admissionDate ? moment(admissionDate) : moment()
            }
            disabledDate={disabledStartDate}
            rules={[
              {
                required: true,
                message: t(
                  "certificationTable.messages.completionDateRequired"
                ),
              },
            ]}
          />

          <Form.Item
            name="beforeAdmission"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox defaultChecked={true} disabled={true}>
              {t("certificationTable.labels.beforeAdmission")}
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
export default AdnCertificationTable;
